import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  chartContainer: {
    marginTop: 10,
  },
  explanationText: {
    fontSize: 8,
    color: '#666666',
    lineHeight: 1.4,
    marginBottom: 15,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  categoryLabel: {
    width: '25%',
    fontSize: 8,
    paddingRight: 10,
  },
  barContainer: {
    width: '75%',
    height: 20,
    flexDirection: 'row',
  },
  // Updated colors with more distinction
  phaseBar1: {
    backgroundColor: '#FF6B6B', // Bright Coral
  },
  phaseBar2: {
    backgroundColor: '#4ECDC4', // Medium Turquoise
  },
  phaseBar3: {
    backgroundColor: '#6C5CE7', // Soft Purple
  },
  phaseBar4: {
    backgroundColor: '#A8E6CF', // Mint Green
  },
  phaseBar5: {
    backgroundColor: '#FFD93D', // Bright Yellow
  },
  phaseBar6: {
    backgroundColor: '#FF8B94', // Soft Pink
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginTop: 15,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 15,
    marginBottom: 5,
    width: '30%',
  },
  legendColor: {
    width: 12,
    height: 12,
    marginRight: 5,
  },
  legendText: {
    fontSize: 7,
  },
  // Updated and new styles for details section
  detailsSection: {
    marginTop: 20,
    borderTop: '1 solid #e0e0e0',
    paddingTop: 10,
  },
  categoryDetails: {
    marginBottom: 15,
    backgroundColor: '#f5f5f5', // Light grey background
    padding: 10,
    borderRadius: 4,
  },
  categoryTitle: {
    fontSize: 9,
    fontWeight: 600,
    marginBottom: 5,
    color: '#333333',
  },
  phaseDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#ffffff', // White background for phase details
    padding: 8,
    borderRadius: 3,
    marginTop: 5,
  },
  phaseItem: {
    width: '33%',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
    paddingRight: 5,
  },
  phaseCount: {
    fontSize: 7,
    color: '#666666',
  },
  totalCount: {
    fontSize: 8,
    color: '#444444',
    marginBottom: 5,
    paddingLeft: 2,
  }
});

const PHASES = {
  EMPATHIZE: 'Empathize',
  DEFINE: 'Define',
  IDEATE: 'Ideate',
  PROTOTYPE: 'Prototype',
  TEST: 'Test',
  COMMUNICATE: 'Communicate'
};

const CATEGORIES = {
  SKILLS: 'skills',
  KNOWLEDGE: 'knowledge',
  MINDSET: 'mindset',
  INSTRUMENTS: 'tool'
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Breakdown = ({ data }) => {
  if (!data) return null;

  const categories = Object.entries(data).map(([category, phases]) => ({
    name: capitalizeFirstLetter(category),
    phases: [
      phases[PHASES.EMPATHIZE] || 0,
      phases[PHASES.DEFINE] || 0,
      phases[PHASES.IDEATE] || 0,
      phases[PHASES.PROTOTYPE] || 0,
      phases[PHASES.TEST] || 0,
      phases[PHASES.COMMUNICATE] || 0
    ]
  }));

  // Calculate total competencies and phase counts for each category
  const categoryDetails = Object.entries(data).map(([category, phaseData]) => {
    const { total, ...phases } = phaseData;
    
    // Calculate actual count for each phase based on percentage
    const phaseValues = Object.entries(phases).map(([phase, percentage]) => ({
      phase,
      percentage,
      count: Math.round((percentage / 100) * total)
    }));

    return {
      category: capitalizeFirstLetter(category),
      phases: phaseValues,
      total
    };
  });

  return (
    <View style={styles.container}>
      <Text style={styles.explanationText}>
        Shows competency distribution across design thinking phases for each category. Each bar represents a category, colored segments show the percentage of competencies in each phase. For each category, percentages show what portion of competencies in that category belong to each phase. For example, if Skills shows 60% Empathize, it means 60% of all selected Skills competencies are in the Empathize phase. Percentages within each category always add up to 100%.
      </Text>
      <View style={styles.chartContainer}>
        {categories.map((category, index) => {
          const hasCompetencies = category.phases.some(phase => phase > 0);
          
          return (
            <View key={index} style={styles.barRow}>
              <Text style={styles.categoryLabel}>{category.name}</Text>
              <View style={styles.barContainer}>
                {hasCompetencies ? (
                  category.phases.map((phase, phaseIndex) => (
                    <View
                      key={phaseIndex}
                      style={[
                        phaseIndex === 0 && styles.phaseBar1,
                        phaseIndex === 1 && styles.phaseBar2,
                        phaseIndex === 2 && styles.phaseBar3,
                        phaseIndex === 3 && styles.phaseBar4,
                        phaseIndex === 4 && styles.phaseBar5,
                        phaseIndex === 5 && styles.phaseBar6,
                        { width: `${phase}%` }
                      ]}
                    />
                  ))
                ) : (
                  <View
                    style={{
                      backgroundColor: '#e0e0e0',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                )}
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.legend}>
        {Object.values(PHASES).map((phase, index) => {
          const getPhaseBarStyle = (index) => {
            switch (index) {
              case 0: return styles.phaseBar1;
              case 1: return styles.phaseBar2;
              case 2: return styles.phaseBar3;
              case 3: return styles.phaseBar4;
              case 4: return styles.phaseBar5;
              case 5: return styles.phaseBar6;
              default: return styles.phaseBar1;
            }
          };

          return (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.legendColor, getPhaseBarStyle(index)]} />
              <Text style={styles.legendText}>{phase}</Text>
            </View>
          );
        })}
      </View>

      <View style={styles.detailsSection}>
        {categoryDetails.map((category, index) => (
          <View key={index} style={styles.categoryDetails}>
            <Text style={styles.categoryTitle}>{category.category}</Text>
            <Text style={styles.totalCount}>
              Total: {category.total} competencies
            </Text>
            <View style={styles.phaseDetails}>
              {category.phases.map((phase, phaseIndex) => (
                phase.percentage > 0 && (
                  <View key={phaseIndex} style={styles.phaseItem}>
                    <View 
                      style={[
                        styles.legendColor,
                        (() => {
                          const phaseIndex = Object.values(PHASES).indexOf(phase.phase);
                          switch (phaseIndex) {
                            case 0: return styles.phaseBar1;
                            case 1: return styles.phaseBar2;
                            case 2: return styles.phaseBar3;
                            case 3: return styles.phaseBar4;
                            case 4: return styles.phaseBar5;
                            case 5: return styles.phaseBar6;
                            default: return styles.phaseBar1;
                          }
                        })()
                      ]} 
                    />
                    <Text style={styles.phaseCount}>
                      {phase.phase}: {phase.percentage}% ({phase.count})
                    </Text>
                  </View>
                )
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

export default Breakdown; 